import { useEffect, useState } from 'react';
import BlockIcon from '@mui/icons-material/Block';
import { IconButton, Popover, TextField, Tooltip } from '@mui/material';
import { hexToHsva, hsvaToHex, type HsvaColor } from '@uiw/color-convert';
import ShadeSlider from '@uiw/react-color-shade-slider';
import Wheel from '@uiw/react-color-wheel';
import { useTranslation } from 'react-i18next';
import * as S from '../../../styles';
import { BUTTONS, DIALOGS } from '../../../TransportCloudTable.consts';

type Props = {
  anchorEl: HTMLElement | null;
  value: string | null;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: (color: string | null) => void;
};

export const ColorPickerPopover = ({
  anchorEl,
  value,
  disabled,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation(['misc']);
  const [hsva, setHsva] = useState<HsvaColor>(() =>
    hexToHsva(value ?? '#378AD'),
  );
  const hex = hsvaToHex(hsva);
  const [hexInput, setHexInput] = useState(hex);

  useEffect(() => {
    setHexInput(hex);
  }, [hex]);

  const handleHexInput = (next: string) => {
    setHexInput(next);
    if (/^#[0-9a-fA-F]{6}$/.test(next)) setHsva(hexToHsva(next));
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ sx: S.colorPickerPaperSx }}
    >
      <S.ColorPickerBody>
        <Wheel
          color={hsva}
          width={168}
          height={168}
          onChange={(color) => setHsva({ ...hsva, ...color.hsva })}
        />
        <ShadeSlider
          hsva={hsva}
          width={168}
          onChange={(shade) => setHsva({ ...hsva, ...shade })}
        />
        <S.ColorPickerRow>
          <S.ColorSwatch $color={hex} $size={32} />
          <TextField
            size='small'
            variant='outlined'
            label={DIALOGS.hexLabel}
            value={hexInput}
            onChange={(event) => handleHexInput(event.target.value)}
            inputProps={{ maxLength: 7, spellCheck: false }}
            sx={S.hexInputSx}
          />
          <Tooltip title={DIALOGS.noColorTooltip}>
            <span>
              <IconButton
                size='small'
                disabled={disabled}
                aria-label={DIALOGS.noColorTooltip}
                onClick={() => onConfirm(null)}
              >
                <BlockIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </S.ColorPickerRow>
      </S.ColorPickerBody>

      <S.ModalActions>
        <S.TCStyledButton
          $primary
          disabled={disabled}
          onClick={() => onConfirm(hex)}
        >
          {BUTTONS.save}
        </S.TCStyledButton>
        <S.TCStyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </S.TCStyledButton>
      </S.ModalActions>
    </Popover>
  );
};
